// Phase 7 — Track B T-B2. Form primitive (RHF wrapper).
//
// Thin wrapper around `<FormProvider>` from `react-hook-form`. The plan
// §2.8 names this primitive as the entry point for every Phase 7 form;
// downstream tracks (D — payments writes, E — dates writes, C — settings,
// F — Gmail OAuth UI) compose against it.
//
// Default layout uses the existing `<Stack>` primitive (vertical, gap=4)
// to match Phase 6 design system conventions. Consumers can override the
// className via standard HTML attributes.
//
// The wrapper accepts a strongly-typed `onSubmit` handler whose argument
// is inferred from the supplied `UseFormReturn`. Per plan §2.10, when the
// form has multiple validation errors after submit, the first-named field
// is focused via `setFocus`. That focus call is delegated to FormError
// (which sees `formState.errors` as the source of truth) — Form itself
// just intercepts the submit and reroutes through RHF's handleSubmit so
// invalid submissions never reach the consumer.
//
// Tokens: relies on the existing surface / spacing tokens applied by the
// Stack primitive; this file adds no new token classes.

import * as React from 'react';
import {
  FormProvider,
  type FieldValues,
  type SubmitHandler,
  type SubmitErrorHandler,
  type UseFormReturn,
} from 'react-hook-form';
import { cn } from '../../lib/cn.js';

export interface FormProps<TValues extends FieldValues>
  extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit' | 'onError' | 'onInvalid'> {
  /** RHF context returned from `useForm()` in the parent. */
  form: UseFormReturn<TValues>;
  /** Called with parsed values after RHF validation passes. */
  onSubmit: SubmitHandler<TValues>;
  /** Optional invalid-submit hook; defaults to focusing the first errored field. */
  onInvalid?: SubmitErrorHandler<TValues>;
  children: React.ReactNode;
}

/**
 * Type-safe RHF form wrapper. Usage:
 *
 *   const form = useForm<MyValues>({ resolver: zodResolver(mySchema) });
 *   return (
 *     <Form form={form} onSubmit={(values) => mutate(values)}>
 *       <FormField name="email" label="Email"><Input /></FormField>
 *       <FormSubmit>Save</FormSubmit>
 *     </Form>
 *   );
 */
export function Form<TValues extends FieldValues>({
  form,
  onSubmit,
  onInvalid,
  className,
  children,
  ...rest
}: FormProps<TValues>): JSX.Element {
  // Default invalid-handler focuses the first errored field per plan §2.10.
  // RHF's setError + setFocus pair is the canonical way; we only fire it
  // when the consumer hasn't supplied their own onInvalid.
  const handleInvalid: SubmitErrorHandler<TValues> = React.useCallback(
    (errors, event) => {
      if (onInvalid) {
        onInvalid(errors, event);
        return;
      }
      const firstKey = Object.keys(errors)[0];
      if (firstKey !== undefined) {
        try {
          // setFocus is generic over the field path; the key is a string
          // path that RHF understands.
          (form.setFocus as (name: string) => void)(firstKey);
        } catch {
          // setFocus throws if the field is not registered yet; harmless.
        }
      }
    },
    [form, onInvalid],
  );

  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit, handleInvalid)}
        className={cn('flex flex-col gap-4', className)}
        {...rest}
      >
        {children}
      </form>
    </FormProvider>
  );
}
Form.displayName = 'Form';
